import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  src: string;
  /** Aspect hint — true for 1080x1920 reels. */
  vertical?: boolean;
  className?: string;
  /**
   * When true, the video loads only once its card enters the viewport
   * (IntersectionObserver). Default for dashboard cards so 7+ videos
   * don't all autoplay at once. Detail views can pass `lazy={false}`
   * since there's only one video on screen.
   */
  lazy?: boolean;
}

/**
 * Instagram-feel video player.
 *
 * - Muted autoplay + loop by default (matches Reels muted-default UX)
 * - Click once to unmute; subsequent clicks toggle play/pause
 * - `lazy`: don't even request the mp4 until the card scrolls into view.
 *   Crucial for the Dashboard, which previously tried to autoplay 7+
 *   reels simultaneously — froze older browsers and slow networks.
 */
export function VideoPlayer({
  src,
  vertical = true,
  className = "",
  lazy = true,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [visible, setVisible] = useState(!lazy);

  // Observe our own container — start loading the video only when it
  // enters the viewport. Once revealed, stay revealed (no unload on
  // scroll-off; the browser handles that via its own memory budget).
  useEffect(() => {
    if (!lazy || visible) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      // Start loading 200px before the card enters the viewport — the
      // video can begin buffering before the user sees the black frame.
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [lazy, visible]);

  const onClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (muted) {
      v.muted = false;
      setMuted(false);
    } else if (v.paused) {
      void v.play();
    } else {
      v.pause();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-2xl bg-black shadow-lg ${className}`}
      style={vertical ? { aspectRatio: "9 / 16" } : undefined}
    >
      {visible ? (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop
          muted={muted}
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          onClick={onClick}
        />
      ) : (
        // Placeholder while offscreen — indicates a loadable video without
        // spending the bandwidth or decoder cycles. Tappable so a user can
        // force-load by tapping even if the observer hasn't fired.
        <button
          type="button"
          onClick={() => setVisible(true)}
          className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-blue to-brand-purple text-brand-cream/80 transition hover:text-brand-cream"
          aria-label="Reel laden"
        >
          <svg viewBox="0 0 48 48" className="h-12 w-12 opacity-80" fill="currentColor">
            <path d="M14 10v28l26-14L14 10z" />
          </svg>
        </button>
      )}
      {visible && muted ? (
        <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
          Tap for sound
        </div>
      ) : null}
    </div>
  );
}
